import type { PublicationTestStatus } from '@/types/automationPublications.ts';

export type PublicationTestStatusCounts = Pick<
  PublicationTestStatus,
  | 'overall_status'
  | 'total_configured_tests'
  | 'passed'
  | 'failed'
  | 'not_run'
  | 'running'
  | 'incomplete'
  | 'repair_needed'
>;

function testCountLabel(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function publicationTestStatusSummary(
  status: PublicationTestStatusCounts | null | undefined,
) {
  if (!status) return 'Test evidence could not be loaded.';
  if (status.overall_status === 'no_tests') return 'No configured tests.';
  if (status.overall_status === 'passing')
    return `All ${testCountLabel(status.total_configured_tests ?? status.passed, 'configured test')} passed.`;

  const counts = [
    status.passed && testCountLabel(status.passed, 'passed', 'passed'),
    status.failed && testCountLabel(status.failed, 'failed', 'failed'),
    status.not_run && testCountLabel(status.not_run, 'not run', 'not run'),
    status.running && testCountLabel(status.running, 'running', 'running'),
    status.incomplete && testCountLabel(status.incomplete, 'incomplete', 'incomplete'),
    status.repair_needed &&
      `${testCountLabel(status.repair_needed, 'test')} ${status.repair_needed === 1 ? 'needs' : 'need'} repair`,
  ]
    .filter(Boolean)
    .join(' · ');
  if (counts) return counts;
  return status.overall_status === 'needs_repair'
    ? 'Test evidence needs repair.'
    : 'Test status needs attention.';
}

export function publicationTestStatusAcknowledgement(
  status: PublicationTestStatusCounts | null | undefined,
) {
  if (!status) return 'I understand that test evidence is unavailable and want to publish.';
  if (status.overall_status === 'no_tests')
    return 'I understand that no tests are configured and want to publish.';

  const concerns = [
    status.failed && testCountLabel(status.failed, 'test failed', 'tests failed'),
    status.not_run && testCountLabel(status.not_run, 'test has not run', 'tests have not run'),
    status.running && testCountLabel(status.running, 'test is still running', 'tests are still running'),
    status.incomplete && testCountLabel(status.incomplete, 'test is incomplete', 'tests are incomplete'),
    status.repair_needed &&
      testCountLabel(status.repair_needed, 'test needs repair', 'tests need repair'),
  ].filter(Boolean);
  if (concerns.length === 1)
    return `I understand that ${concerns[0]} and want to publish.`;
  if (status.overall_status === 'needs_repair')
    return 'I understand that test evidence needs repair and want to publish.';
  return 'I understand that test status needs attention and want to publish.';
}
